<script setup lang="ts">
import { computed, nextTick, ref, watchEffect } from "vue";
import { Bot, Cpu, Send, Square, Trash2, User, Wrench } from "lucide-vue-next";
import { api } from "../services/api";
import type { AgentSummary } from "../types";

type ChatEntry = {
  id: number;
  role: "user" | "assistant";
  content: string;
  error?: boolean;
  pending?: boolean;
  omitFromContext?: boolean;
};
const props = defineProps<{ agents: AgentSummary[] }>();
const selectedAgent = ref("");
const prompt = ref("");
const includeDeviceContext = ref(false);
const messages = ref<ChatEntry[]>([]);
const running = ref(false);
const controller = ref<AbortController>();
const scrollArea = ref<HTMLElement>();
let sequence = 0;

const textAgents = computed(() => props.agents.filter((agent) => agent.capabilities.text));
const activeAgent = computed(() => textAgents.value.find((agent) => agent.slug === selectedAgent.value) ?? textAgents.value[0]);
const deviceToolsEnabled = computed(() => Boolean(activeAgent.value?.capabilities.deviceControl));

watchEffect(() => {
  if (!selectedAgent.value && textAgents.value[0]) selectedAgent.value = textAgents.value[0].slug;
});

async function scrollBottom(): Promise<void> {
  await nextTick();
  if (scrollArea.value) scrollArea.value.scrollTop = scrollArea.value.scrollHeight;
}

async function send(): Promise<void> {
  const value = prompt.value.trim();
  if (!value || !activeAgent.value || running.value) return;
  const userMessage: ChatEntry = { id: ++sequence, role: "user", content: value };
  const assistantMessage: ChatEntry = { id: ++sequence, role: "assistant", content: "正在理解请求...", pending: true };
  messages.value.push(userMessage, assistantMessage);
  prompt.value = "";
  running.value = true;
  const requestController = new AbortController();
  controller.value = requestController;
  await scrollBottom();

  try {
    await api.streamChat({
      agent: activeAgent.value.slug,
      messages: messages.value
        .filter((item) => !item.error && !item.pending && !item.omitFromContext)
        .map((item) => ({ role: item.role, content: item.content })),
      includeDeviceContext: includeDeviceContext.value,
      signal: requestController.signal,
      onEvent(event, data) {
        if (event === "tool_started") assistantMessage.content = "正在下发设备命令并等待响应...";
        if (event === "tool_completed") assistantMessage.content = "设备已响应，正在生成回复...";
        if (event === "tool_failed") assistantMessage.content = "设备命令未完成，正在整理原因...";
        if (event === "delta") {
          if (assistantMessage.pending) assistantMessage.content = "";
          assistantMessage.pending = false;
          assistantMessage.content += String(data.text ?? "");
        }
        if (event === "error") {
          assistantMessage.pending = false;
          assistantMessage.error = true;
          const detail = typeof data.details === "string"
            ? data.details
            : data.details ? JSON.stringify(data.details) : "";
          assistantMessage.content = `${String(data.code ?? "ERROR")}: ${String(data.message ?? "调用失败")}${detail ? `\n${detail}` : ""}`;
        }
        void scrollBottom();
      },
    });
    assistantMessage.pending = false;
    if (!assistantMessage.content) assistantMessage.content = "未收到文字内容";
  } catch (error) {
    if (requestController.signal.aborted) {
      userMessage.omitFromContext = true;
      assistantMessage.pending = false;
      assistantMessage.error = true;
      assistantMessage.content = "已停止";
    } else {
      assistantMessage.pending = false;
      assistantMessage.error = true;
      assistantMessage.content = error instanceof Error ? error.message : "调用失败";
    }
  } finally {
    if (controller.value === requestController) controller.value = undefined;
    running.value = false;
  }
}

function stop(): void {
  controller.value?.abort();
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    void send();
  }
}
</script>

<template>
  <div class="chat-workspace">
    <div class="chat-toolbar">
      <label class="compact-field">
        <span>智能体</span>
        <select v-model="selectedAgent">
          <option value="" disabled>{{ textAgents.length ? "请选择" : "没有可用智能体" }}</option>
          <option v-for="agent in textAgents" :key="agent.slug" :value="agent.slug">{{ agent.name }}</option>
        </select>
      </label>
      <span v-if="deviceToolsEnabled" class="tool-enabled-badge" title="该智能体可以调用已授权的设备控制工具">
        <Wrench :size="14" />设备工具已启用
      </span>
      <label class="toggle-control">
        <input v-model="includeDeviceContext" type="checkbox" />
        <span class="toggle-track" />
        <Cpu :size="16" />
        <span>设备状态上下文</span>
      </label>
      <button class="icon-button compact" type="button" title="清空对话" :disabled="!messages.length" @click="messages = []">
        <Trash2 :size="17" />
      </button>
    </div>

    <section ref="scrollArea" class="conversation">
      <div v-if="!messages.length" class="conversation-empty">
        <div class="empty-icon"><Bot :size="30" /></div>
        <strong>等待提问</strong>
        <span>{{ activeAgent ? `当前智能体：${activeAgent.name}` : "请先配置智能体" }}</span>
      </div>
      <article v-for="message in messages" :key="message.id" :class="['message-row', message.role, { error: message.error }]">
        <div class="message-avatar"><User v-if="message.role === 'user'" :size="17" /><Bot v-else :size="17" /></div>
        <div class="message-body">
          <span>{{ message.role === "user" ? "你" : "智能体" }}</span>
          <p>{{ message.content || "正在生成..." }}</p>
        </div>
      </article>
    </section>

    <div class="composer">
      <textarea v-model="prompt" rows="3" placeholder="输入消息，例如：打开客厅灯" :disabled="running" @keydown="onKeydown" />
      <button v-if="running" class="send-button stop" type="button" title="停止" @click="stop"><Square :size="18" /></button>
      <button v-else class="send-button" type="button" title="发送" :disabled="!prompt.trim() || !activeAgent" @click="send"><Send :size="18" /></button>
    </div>
  </div>
</template>
